import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  User, Car, Wrench, DollarSign, Edit, Phone, Mail, MapPin, CreditCard,
  AlertCircle, CheckCircle2, Clock, ChevronLeft, Plus, Trash2, History, FileText, Calendar, PlusCircle
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import HistoricoVeiculoModal from './HistoricoVeiculoModal';

const fmtBRL = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtDate = (d) => (d ? new Date(d.includes('T') ? d : d + 'T00:00:00').toLocaleDateString('pt-BR') : '-');

const STATUS_OS = {
  aguardando: { label: 'Aguardando', cor: 'text-blue-300 bg-blue-500/10' },
  em_andamento: { label: 'Em andamento', cor: 'text-amber-300 bg-amber-500/10' },
  concluido: { label: 'Concluído', cor: 'text-emerald-300 bg-emerald-500/10' },
  cancelado: { label: 'Cancelado', cor: 'text-red-300 bg-red-500/10' },
};

const FORMAS_PAGTO = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_debito', label: 'Cartão Débito' },
  { value: 'cartao_credito', label: 'Cartão Crédito' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'crypto', label: 'Criptomoeda' },
];

export default function ClientePerfilModal({ open, onOpenChange, clienteId, onEditarCadastro }) {
  const [view, setView] = useState('overview'); // 'overview' | 'veiculos'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [veiculoFormOpen, setVeiculoFormOpen] = useState(false);
  const [veiculoForm, setVeiculoForm] = useState({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
  const [veiculoEditing, setVeiculoEditing] = useState(null);
  const [historicoVeiculo, setHistoricoVeiculo] = useState(null);
  const [pagar, setPagar] = useState(null); // transação pendente
  const [pagarForma, setPagarForma] = useState('dinheiro');
  const [pagarObs, setPagarObs] = useState('');
  const [saldoOpen, setSaldoOpen] = useState(false);
  const [saldoForm, setSaldoForm] = useState({ valor: '', descricao: '', data_vencimento: new Date().toISOString().slice(0, 10) });
  const [savingSaldo, setSavingSaldo] = useState(false);
  const [pagAvulsoOpen, setPagAvulsoOpen] = useState(false);
  const [pagAvulsoForm, setPagAvulsoForm] = useState({ valor: '', forma: 'dinheiro', observacao: '' });
  const [savingPagAvulso, setSavingPagAvulso] = useState(false);

  const carregar = async () => {
    if (!clienteId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/clientes/${clienteId}/perfil-completo`);
      setData(data);
    } catch (err) {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  // Carrega quando muda clienteId (não usa useEffect para evitar set-state-in-effect)
  const [lastLoadedId, setLastLoadedId] = useState(null);
  if (open && clienteId && clienteId !== lastLoadedId) {
    setLastLoadedId(clienteId);
    carregar();
  }

  const confirmarPagamento = async () => {
    if (!pagar) return;
    try {
      await api.post(`/transacoes/${pagar.id}/confirmar-pagamento`, { forma: pagarForma, observacao: pagarObs });
      toast.success('Pagamento confirmado');
      setPagar(null); setPagarObs('');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar');
    }
  };

  const registrarPagamentoAvulso = async () => {
    const valor = parseFloat(pagAvulsoForm.valor);
    if (!valor || valor <= 0) {
      toast.error('Informe um valor maior que zero');
      return;
    }
    if (valor > (resumo.saldo_devedor || 0) + 0.009) {
      toast.error(`Valor excede o saldo devedor (${fmtBRL(resumo.saldo_devedor)})`);
      return;
    }
    setSavingPagAvulso(true);
    try {
      const { data: res } = await api.post(`/clientes/${clienteId}/pagamento-avulso`, {
        valor,
        forma: pagAvulsoForm.forma,
        observacao: pagAvulsoForm.observacao,
      });
      const quitadas = (res.afetadas || []).filter((a) => a.quitada).length;
      const parciais = (res.afetadas || []).filter((a) => !a.quitada).length;
      toast.success(
        `Pagamento de ${fmtBRL(valor)} registrado. ${quitadas} pendência(s) quitada(s)${parciais ? `, ${parciais} parcial` : ''}.`
      );
      setPagAvulsoOpen(false);
      setPagAvulsoForm({ valor: '', forma: 'dinheiro', observacao: '' });
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar pagamento');
    } finally {
      setSavingPagAvulso(false);
    }
  };

  const adicionarSaldoDevedor = async () => {
    const valor = parseFloat(saldoForm.valor);
    if (!valor || valor <= 0) {
      toast.error('Informe um valor maior que zero');
      return;
    }
    if (!saldoForm.descricao.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }
    setSavingSaldo(true);
    try {
      await api.post(`/clientes/${clienteId}/saldo-devedor`, {
        valor,
        descricao: saldoForm.descricao.trim(),
        data_vencimento: saldoForm.data_vencimento || null,
      });
      toast.success('Saldo devedor adicionado');
      setSaldoOpen(false);
      setSaldoForm({ valor: '', descricao: '', data_vencimento: new Date().toISOString().slice(0, 10) });
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao adicionar');
    } finally {
      setSavingSaldo(false);
    }
  };

  const salvarVeiculo = async () => {
    if (!veiculoForm.placa || !veiculoForm.marca || !veiculoForm.modelo) {
      toast.error('Placa, marca e modelo são obrigatórios');
      return;
    }
    try {
      const payload = {
        cliente_id: clienteId,
        placa: veiculoForm.placa.toUpperCase(),
        marca: veiculoForm.marca,
        modelo: veiculoForm.modelo,
        ano: veiculoForm.ano || null,
        cor: veiculoForm.cor || null,
      };
      if (veiculoEditing) {
        await api.put(`/veiculos/${veiculoEditing.id}`, payload);
        toast.success('Veículo atualizado');
      } else {
        await api.post('/veiculos', payload);
        toast.success('Veículo cadastrado');
      }
      setVeiculoFormOpen(false); setVeiculoEditing(null);
      setVeiculoForm({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const excluirVeiculo = async (v) => {
    if (!window.confirm(`Excluir ${v.marca} ${v.modelo} (${v.placa})?`)) return;
    try {
      await api.delete(`/veiculos/${v.id}`);
      toast.success('Veículo excluído');
      carregar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const editarVeiculo = (v) => {
    setVeiculoEditing(v);
    setVeiculoForm({ placa: v.placa || '', marca: v.marca || '', modelo: v.modelo || '', ano: v.ano || '', cor: v.cor || '' });
    setVeiculoFormOpen(true);
  };

  const novoVeiculo = () => {
    setVeiculoEditing(null);
    setVeiculoForm({ placa: '', marca: '', modelo: '', ano: '', cor: '' });
    setVeiculoFormOpen(true);
  };

  if (!open) return null;

  const cliente = data?.cliente;
  const resumo = data?.resumo || {};
  const pendencias = data?.pendencias || [];
  const ordens = data?.ordens || [];
  const veiculos = data?.veiculos || [];

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setView('overview'); } onOpenChange(o); }}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="cliente-perfil-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-3">
              {view === 'veiculos' ? (
                <Button variant="ghost" size="sm" onClick={() => setView('overview')} className="text-slate-400 hover:text-white -ml-2">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Voltar
                </Button>
              ) : (
                <User className="w-5 h-5 text-blue-400" />
              )}
              {view === 'overview' ? `Perfil de ${cliente?.nome || ''}` : 'Veículos do Cliente'}
            </DialogTitle>
          </DialogHeader>

          {loading || !data ? (
            <div className="text-center py-8 text-slate-400">Carregando...</div>
          ) : view === 'overview' ? (
            <>
              {/* Cards de resumo */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
                  <p className="text-[10px] uppercase text-emerald-300">Pago</p>
                  <p className="text-lg font-bold text-emerald-400">{fmtBRL(resumo.total_pago)}</p>
                </div>
                <div className={`border rounded p-3 ${resumo.saldo_devedor > 0 ? 'bg-amber-500/10 border-amber-500/40' : 'bg-slate-800 border-white/5'}`}>
                  <p className="text-[10px] uppercase text-slate-300">Saldo Devedor</p>
                  <p className={`text-lg font-bold ${resumo.saldo_devedor > 0 ? 'text-amber-300' : 'text-slate-400'}`}>{fmtBRL(resumo.saldo_devedor)}</p>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/30 rounded p-3">
                  <p className="text-[10px] uppercase text-blue-300">OS</p>
                  <p className="text-lg font-bold text-blue-400">{resumo.total_ordens}</p>
                </div>
                <div className="bg-slate-800 border border-white/10 rounded p-3 cursor-pointer hover:border-blue-500/40" onClick={() => setView('veiculos')} data-testid="ir-veiculos">
                  <p className="text-[10px] uppercase text-slate-300">Veículos →</p>
                  <p className="text-lg font-bold text-white">{resumo.total_veiculos}</p>
                </div>
              </div>

              {/* Dados pessoais + ações */}
              <div className="bg-obsidian border border-white/10 rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-300 uppercase">Dados pessoais</h3>
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setView('veiculos')} className="text-blue-400 hover:bg-blue-500/10 h-8" data-testid="btn-veiculos">
                      <Car className="w-3.5 h-3.5 mr-1" /> Veículos
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => { onOpenChange(false); onEditarCadastro?.(cliente); }} className="text-emerald-400 hover:bg-emerald-500/10 h-8" data-testid="btn-editar-cadastro">
                      <Edit className="w-3.5 h-3.5 mr-1" /> Editar Cadastro
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  {cliente?.cpf_cnpj && <div><span className="text-slate-500 text-xs">CPF/CNPJ:</span> <span className="text-white">{cliente.cpf_cnpj}</span></div>}
                  {cliente?.telefone && <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-500" /> <span className="text-white">{cliente.telefone}</span></div>}
                  {cliente?.email && <div className="flex items-center gap-1 md:col-span-2"><Mail className="w-3 h-3 text-slate-500" /> <span className="text-white">{cliente.email}</span></div>}
                  {(cliente?.endereco || cliente?.cidade) && (
                    <div className="flex items-start gap-1 md:col-span-2">
                      <MapPin className="w-3 h-3 text-slate-500 mt-0.5" />
                      <span className="text-white text-xs">{[cliente.endereco, cliente.numero, cliente.bairro, cliente.cidade, cliente.estado, cliente.cep].filter(Boolean).join(', ')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Pendências */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                  <h3 className="text-sm font-semibold text-amber-300 uppercase flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Pendências financeiras ({pendencias.length})
                  </h3>
                  <div className="flex gap-2 flex-wrap">
                    {resumo.saldo_devedor > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => {
                        setPagAvulsoForm({ valor: '', forma: 'dinheiro', observacao: '' });
                        setPagAvulsoOpen(true);
                      }}
                        className="text-emerald-400 hover:bg-emerald-500/10 h-8 normal-case border border-emerald-500/30"
                        data-testid="btn-pag-avulso">
                        <DollarSign className="w-3.5 h-3.5 mr-1" /> Registrar Pagamento
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => setSaldoOpen(true)}
                      className="text-amber-400 hover:bg-amber-500/10 h-8 normal-case" data-testid="btn-add-saldo">
                      <PlusCircle className="w-3.5 h-3.5 mr-1" /> Adicionar Saldo Devedor
                    </Button>
                  </div>
                </div>
                {pendencias.length === 0 ? (
                  <div className="bg-obsidian border border-white/5 rounded p-4 text-center text-slate-500 text-xs">
                    Nenhuma pendência. Use o botão acima para registrar débitos avulsos.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {pendencias.map((t) => (
                      <div key={t.id} className="bg-amber-500/5 border border-amber-500/20 rounded p-3 flex items-center justify-between gap-3" data-testid={`pendencia-${t.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-medium truncate">
                            {t.descricao}
                            {t.avulso && <span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">Avulso</span>}
                          </p>
                          <p className="text-xs text-slate-400">
                            Venc: {fmtDate(t.data_vencimento || t.data)}
                            {t.status_solicitacao === 'aguardando_confirmacao' && (
                              <span className="ml-2 text-blue-300">• Cliente solicitou: {t.solicitado_forma}</span>
                            )}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="font-bold text-amber-400">{fmtBRL(t.valor)}</p>
                        </div>
                        <Button size="sm" onClick={() => { setPagar(t); setPagarForma(t.solicitado_forma || t.forma_pagamento || 'dinheiro'); }}
                          className="bg-emerald-500 hover:bg-emerald-600 text-white h-8" data-testid={`btn-pagar-${t.id}`}>
                          <DollarSign className="w-3.5 h-3.5 mr-1" /> Pagar
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Histórico de serviços */}
              <div>
                <h3 className="text-sm font-semibold text-slate-300 uppercase mb-2 flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-blue-400" /> Histórico de Serviços ({ordens.length})
                </h3>
                {ordens.length === 0 ? (
                  <div className="bg-obsidian border border-white/5 rounded p-6 text-center text-slate-500 text-sm">Nenhuma ordem de serviço</div>
                ) : (
                  <div className="space-y-1.5 max-h-60 overflow-y-auto">
                    {ordens.map((o) => {
                      const meta = STATUS_OS[o.status] || STATUS_OS.aguardando;
                      const veic = veiculos.find(v => v.id === o.veiculo_id);
                      return (
                        <div key={o.id} className="bg-obsidian border border-white/5 rounded p-2.5 flex items-center justify-between gap-3 text-sm">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-mono text-xs">#{o.id.slice(-6)}</p>
                              <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${meta.cor}`}>{meta.label}</span>
                            </div>
                            <p className="text-xs text-slate-400 truncate mt-0.5">
                              {veic ? `${veic.marca} ${veic.modelo} (${veic.placa})` : '—'} · {fmtDate(o.data_inicio || o.created_at)}
                            </p>
                          </div>
                          <p className="text-white font-semibold text-sm shrink-0">{fmtBRL(o.valor_total)}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            // ===== VIEW: VEICULOS =====
            <>
              <div className="flex justify-end mb-3">
                <Button onClick={novoVeiculo} className="bg-blue-500 hover:bg-blue-600 text-white" size="sm" data-testid="add-veiculo-perfil">
                  <Plus className="w-4 h-4 mr-1" /> Novo Veículo
                </Button>
              </div>
              {veiculos.length === 0 ? (
                <div className="bg-obsidian border border-white/5 rounded p-8 text-center">
                  <Car className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                  <p className="text-slate-400 text-sm">Nenhum veículo cadastrado</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {veiculos.map((v) => (
                    <div key={v.id} className="bg-obsidian border border-white/5 rounded-lg p-3" data-testid={`veiculo-perfil-${v.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-medium">{v.marca} {v.modelo}</p>
                          <p className="text-sm text-slate-400">Placa: <span className="text-white font-mono">{v.placa}</span></p>
                          <p className="text-xs text-slate-500">{v.ano ? `Ano: ${v.ano}` : ''}{v.cor ? ` · Cor: ${v.cor}` : ''}</p>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" onClick={() => editarVeiculo(v)} className="h-8 w-8 p-0 text-blue-400 hover:bg-blue-500/10" title="Editar"><Edit className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => excluirVeiculo(v)} className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/10" title="Excluir"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <Button onClick={() => setHistoricoVeiculo(v)} className="w-full mt-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30" size="sm" data-testid={`hist-veiculo-${v.id}`}>
                        <History className="w-4 h-4 mr-2" /> Ver Histórico do Veículo
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal cadastro/edição de veículo */}
      <Dialog open={veiculoFormOpen} onOpenChange={setVeiculoFormOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>{veiculoEditing ? 'Editar Veículo' : 'Novo Veículo'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase text-slate-400">Placa *</Label>
              <Input value={veiculoForm.placa} onChange={(e) => setVeiculoForm({ ...veiculoForm, placa: e.target.value.toUpperCase() })}
                placeholder="ABC1234" maxLength={8} className="bg-obsidian border-white/10 text-white font-mono" data-testid="perfil-veiculo-placa" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs uppercase text-slate-400">Marca *</Label>
                <Input value={veiculoForm.marca} onChange={(e) => setVeiculoForm({ ...veiculoForm, marca: e.target.value })}
                  className="bg-obsidian border-white/10 text-white" data-testid="perfil-veiculo-marca" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-400">Modelo *</Label>
                <Input value={veiculoForm.modelo} onChange={(e) => setVeiculoForm({ ...veiculoForm, modelo: e.target.value })}
                  className="bg-obsidian border-white/10 text-white" data-testid="perfil-veiculo-modelo" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-400">Ano</Label>
                <Input type="number" value={veiculoForm.ano} onChange={(e) => setVeiculoForm({ ...veiculoForm, ano: e.target.value })}
                  className="bg-obsidian border-white/10 text-white" />
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-400">Cor</Label>
                <Input value={veiculoForm.cor} onChange={(e) => setVeiculoForm({ ...veiculoForm, cor: e.target.value })}
                  className="bg-obsidian border-white/10 text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setVeiculoFormOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button onClick={salvarVeiculo} className="bg-blue-500 hover:bg-blue-600 text-white" data-testid="perfil-veiculo-salvar">
                {veiculoEditing ? 'Atualizar' : 'Cadastrar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal confirmar pagamento de pendência */}
      <Dialog open={!!pagar} onOpenChange={(o) => !o && setPagar(null)}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar Pagamento</DialogTitle>
          </DialogHeader>
          {pagar && (
            <div className="space-y-3">
              <div className="bg-obsidian border border-white/10 rounded p-3">
                <p className="text-xs text-slate-400">{pagar.descricao}</p>
                <p className="text-2xl font-bold text-emerald-400 mt-1">{fmtBRL(pagar.valor)}</p>
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-400">Forma de pagamento</Label>
                <Select value={pagarForma} onValueChange={setPagarForma}>
                  <SelectTrigger className="bg-obsidian border-white/10 text-white" data-testid="pagar-forma-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-carbon border-white/10 text-white">
                    {FORMAS_PAGTO.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase text-slate-400">Observação</Label>
                <Input value={pagarObs} onChange={(e) => setPagarObs(e.target.value)}
                  placeholder="Ex: pago via PIX, comprovante enviado" className="bg-obsidian border-white/10 text-white" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setPagar(null)} className="text-slate-400">Cancelar</Button>
                <Button onClick={confirmarPagamento} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="confirmar-pagto-btn">
                  Confirmar Pagamento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal histórico do veículo */}
      <HistoricoVeiculoModal
        open={!!historicoVeiculo}
        onOpenChange={(o) => !o && setHistoricoVeiculo(null)}
        veiculo={historicoVeiculo}
        apiClient={api}
        mode="admin"
        basePathListar="/veiculos/{id}/historico"
        basePathCrud="/historico"
      />

      {/* Modal adicionar saldo devedor avulso */}
      <Dialog open={saldoOpen} onOpenChange={setSaldoOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-md" data-testid="saldo-devedor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-amber-400" /> Adicionar Saldo Devedor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2 text-xs text-amber-200">
              Registre um débito avulso (fora de OS). Útil para vendas informais, produtos sem nota ou ajustes manuais.
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-400">Valor (R$) *</Label>
              <Input type="number" step="0.01" min="0" value={saldoForm.valor}
                onChange={(e) => setSaldoForm({ ...saldoForm, valor: e.target.value })}
                placeholder="0,00" className="bg-obsidian border-white/10 text-white" data-testid="saldo-valor" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-400">Descrição *</Label>
              <Input value={saldoForm.descricao}
                onChange={(e) => setSaldoForm({ ...saldoForm, descricao: e.target.value })}
                placeholder="Ex: Vela ignição comprada avulsa em 09/06" className="bg-obsidian border-white/10 text-white" data-testid="saldo-descricao" />
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-400">Data de vencimento</Label>
              <Input type="date" value={saldoForm.data_vencimento}
                onChange={(e) => setSaldoForm({ ...saldoForm, data_vencimento: e.target.value })}
                className="bg-obsidian border-white/10 text-white" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setSaldoOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button onClick={adicionarSaldoDevedor} disabled={savingSaldo}
                className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="saldo-confirmar">
                {savingSaldo ? 'Salvando...' : 'Adicionar Débito'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal pagamento avulso/parcial */}
      <Dialog open={pagAvulsoOpen} onOpenChange={setPagAvulsoOpen}>
        <DialogContent className="bg-carbon border-white/10 text-white max-w-md" data-testid="pag-avulso-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-emerald-400" /> Registrar Pagamento Avulso
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded p-2 text-xs text-emerald-200">
              O valor será aplicado nas pendências mais antigas primeiro. Se cobrir parte, o saldo restante permanece pendente.
            </div>
            <div className="bg-obsidian border border-white/10 rounded p-3">
              <p className="text-xs text-slate-400">Saldo devedor atual</p>
              <p className="text-2xl font-bold text-amber-300">{fmtBRL(resumo.saldo_devedor)}</p>
              <p className="text-[10px] text-slate-500 mt-1">{pendencias.length} pendência(s)</p>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-400">Valor pago (R$) *</Label>
              <Input type="number" step="0.01" min="0" max={resumo.saldo_devedor || undefined}
                value={pagAvulsoForm.valor}
                onChange={(e) => setPagAvulsoForm({ ...pagAvulsoForm, valor: e.target.value })}
                placeholder="0,00" className="bg-obsidian border-white/10 text-white text-lg font-bold"
                data-testid="pag-avulso-valor" />
              <div className="flex gap-1 mt-1">
                <Button type="button" size="sm" variant="ghost"
                  onClick={() => setPagAvulsoForm({ ...pagAvulsoForm, valor: String(resumo.saldo_devedor || 0) })}
                  className="text-[10px] h-6 px-2 text-emerald-300 hover:bg-emerald-500/10">
                  Total ({fmtBRL(resumo.saldo_devedor)})
                </Button>
                <Button type="button" size="sm" variant="ghost"
                  onClick={() => setPagAvulsoForm({ ...pagAvulsoForm, valor: (Number(resumo.saldo_devedor || 0) / 2).toFixed(2) })}
                  className="text-[10px] h-6 px-2 text-slate-400 hover:bg-white/5">
                  50%
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-400">Forma de pagamento</Label>
              <Select value={pagAvulsoForm.forma} onValueChange={(v) => setPagAvulsoForm({ ...pagAvulsoForm, forma: v })}>
                <SelectTrigger className="bg-obsidian border-white/10 text-white" data-testid="pag-avulso-forma"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-carbon border-white/10 text-white">
                  {FORMAS_PAGTO.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase text-slate-400">Observação</Label>
              <Input value={pagAvulsoForm.observacao}
                onChange={(e) => setPagAvulsoForm({ ...pagAvulsoForm, observacao: e.target.value })}
                placeholder="Ex: pago em espécie no balcão" className="bg-obsidian border-white/10 text-white"
                data-testid="pag-avulso-obs" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setPagAvulsoOpen(false)} className="text-slate-400">Cancelar</Button>
              <Button onClick={registrarPagamentoAvulso} disabled={savingPagAvulso}
                className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="pag-avulso-confirmar">
                {savingPagAvulso ? 'Registrando...' : 'Registrar Pagamento'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
